import { useMemo } from 'react'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTransactionStatus } from '@/hooks'
import { formatDateTime, formatThousands, toFixedNoRound } from '@/utils'
import { getTransactionIcon } from '../utils'

export default function ConsumptionDetail() {
  const { t } = useTranslation()
  const location = useLocation()
  const { getTransactionStatus } = useTransactionStatus()
  const record = location.state?.record as getUserCardConsumptionRecordListItemResp | undefined

  // 判断交易是否失败
  const isFailed = record?.txn_type === 'authentication_declined'

  // 获取状态显示信息
  const statusInfo = useMemo(() => {
    if (isFailed) {
      return { text: t('失败'), icon: '/images/card-v2/error-status.svg' }
    }
    return { text: t('成功'), icon: '/images/card-v2/success-status.svg' }
  }, [isFailed, t])

  return (
    <PageLayout>
      <Header 
        leftChildren={<Back />} 
        centerChildren={<span className="text-base font-medium">{t('交易详情')}</span>}
      />
      
      <div className="px-5 py-8">
        {/* 顶部金额区域 */}
        <div className="flex flex-col items-center mb-8">
          {/* 图标 */}
          <div className="w-16 h-16 rounded-full bg-[#F2F4F7] flex items-center justify-center mb-4">
            <img src={getTransactionIcon(record?.txn_type)} alt="TYPE_ICON" />
          </div>
          
          {/* 金额 */}
          <div className="text-3xl font-bold text-[#1D2129]">
            {record?.total_amt != null && record.total_amt >= 0 ? '+' : '-'}${formatThousands(Math.abs(record?.total_amt || 0), 2, true)}
          </div>
          
          {/* 状态 */}
          <div className="flex items-center mt-3">
            <img src={statusInfo.icon} alt="STATUS_ICON" />
            <span className="text-sm ml-1">{statusInfo.text}</span>
          </div>
        </div>

        {/* 详情列表 */}
        <div className="space-y-2">
          {/* 日期 */}
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-[#86909C]">{t('日期')}</span>
            <span className="text-sm text-[#1D2129]">
              {formatDateTime(record?.transaction_time)}
            </span>
          </div>

          {/* 卡号 */}
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-[#86909C]">{t('卡号')}</span>
            <span className="text-sm text-[#1D2129]">
              {record?.card?.pan_masked || '--'}
            </span>
          </div>

          {/* 商户名称 */}
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-[#86909C]">{t('商户名称')}</span>
            <span className="text-sm text-[#1D2129]">
              {record?.merch_name || '-'}
            </span>
          </div>

          {/* 交易ID */}
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-[#86909C]">{t('交易ID')}</span>
            <span className="text-sm text-[#1D2129] break-all">
              {record?.provider_txn_id || '-'}
            </span>
          </div>

          {/* 交易类型 */}
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-[#86909C]">{t('交易类型')}</span>
            <span className="text-sm text-[#1D2129]">
              {getTransactionStatus(record?.txn_type || '', undefined, record?.txn_type_string) || '-'}
            </span>
          </div>

          {/* 交易金额 */}
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-[#86909C]">{t('交易金额')}</span>
            <span className="text-sm text-[#1D2129]">
              {formatThousands(toFixedNoRound(record?.bill_amt, 2), 2, true)} {record?.bill_ccy?.toUpperCase() || 'USD'}
            </span>
          </div>

          {/* 授权费 */}
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-[#86909C]">{t('授权费')}</span>
            <span className="text-sm text-[#1D2129]">
              {record?.business_auth_fee !== undefined ? `${formatThousands(toFixedNoRound(record.business_auth_fee, 2), 2, true)} ${record?.bill_ccy?.toUpperCase() || 'USD'}` : '-'}
            </span>
          </div>

          {/* 服务费 */}
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-[#86909C]">{t('服务费')}</span>
            <span className="text-sm text-[#1D2129]">
              {record?.business_service_fee !== undefined ? `${formatThousands(toFixedNoRound(record.business_service_fee, 2), 2, true)} ${record?.bill_ccy?.toUpperCase() || 'USD'}` : '-'}
            </span>
          </div>

          {/* 备注 */}
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-[#86909C]">{t('备注')}</span>
            <span className="text-sm text-[#1D2129]">
              {record?.remark || '--'}
            </span>
          </div>
        </div>
      </div>
    </PageLayout>
  )
}

